import React, { useRef, useState, useEffect, FC, useCallback } from 'react';
import { documentService } from '../../../../../../../../utils/window.utils';
import useSuggestions from './hooks/useSuggestions.effect';
import { TTextSearchProps } from '../../../../../../types/dealerLocator.types';
import googleLogo from '../../../../../../../../assets/powered-by-google-on-white.png';
import ItemList from '../itemList';

const RESULTS_LENGTH_LIMIT = 8;

export const TextSearch: FC<TTextSearchProps> = ({
  countryIsoCode,
  label,
  onGooglePlaceSelect,
  autocompleteService,
  placesService,
  siteLanguage,
  searchInputValue,
  setSearchInputValue,
  onClick,
  updatedSelectedPlaceId,
  prevSelectedResult,
}) => {
  const [searchText, setSearchText] = useState<string>('');
  const [usersInput, setUsersInput] = useState<boolean>(false);
  const suggestionSettings = {
    language: siteLanguage,
    countryIsoCode: countryIsoCode || '',
    resultsLimit: RESULTS_LENGTH_LIMIT,
  };
  const [suggestions] = useSuggestions(searchText, searchInputValue, autocompleteService, suggestionSettings);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [higlightedSuggestionIndex, setHiglightedSuggestionIndex] = useState<number | null>(null);
  const [selectedResult, setSelectedResult] = useState<any>(null);
  const textSearchElmRef = useRef<HTMLDivElement>(null);
  const texInputElmRef = useRef<HTMLInputElement>(null);
  const [selectedPlaceId, setSelectedPlaceId] = useState('');

  const fetchPlaceDetail = useCallback((placeId: string) => {
    if (!placeId) {
      return;
    }

    const placeRequest = {
      placeId: placeId,
      fields: ['geometry'],
    };

    return new Promise<google.maps.places.PlaceResult | null>((resolve, reject) => {
      placesService.getDetails(placeRequest, (result, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          resolve(result);
        } else {
          // eslint-disable-next-line
          reject('Failed to fetch place detail from Google Places API');
        }
      });
    });
  }, []);

  const closeDropdown = (): void => {
    setDropdownOpen(false);
  };

  const getDisplayNameFromResult = (result: any): string => {
    return result?.description || '';
  };

  const textChangeHandler = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newSearchText = e.target?.value || '';
    setUsersInput(true);
    setSearchInputValue(newSearchText);
  };

  const inputClickHandler = (): void => {
    setDropdownOpen(true);
    texInputElmRef?.current?.focus();
  };

  const handleSuggestionMouseEnter = (index: number): void => {
    setHiglightedSuggestionIndex(index);
  };

  const handleSuggestionSelect = (result: any): void => {
    setSelectedPlaceId(result?.place_id);
    setSelectedResult(result);
    updatedSelectedPlaceId(result?.place_id);
    setUsersInput(false);
    texInputElmRef?.current?.blur();
  };

  const handleClickOutside = (e) => {
    if (textSearchElmRef?.current?.contains(e?.target)) {
      return;
    }

    setUsersInput(false);
    closeDropdown();
  };

  const textInputFocusHandler = () => {
    setDropdownOpen(true);
  };

  useEffect(() => {
    if (usersInput) {
      setSearchText(searchInputValue);
      setHiglightedSuggestionIndex(null);
    }
  }, [searchInputValue, usersInput]);

  useEffect(() => {
    const text = getDisplayNameFromResult(selectedResult);

    if (text) {
      setSearchInputValue(text);
    }
  }, [dropdownOpen, selectedResult]);

  useEffect(() => {
    if (prevSelectedResult?.place_id && searchInputValue && !selectedResult) {
      setSelectedResult(prevSelectedResult);
    }
  }, [prevSelectedResult]);

  useEffect(() => {
    const doc = documentService() as Document;

    if (dropdownOpen) {
      doc.addEventListener('click', handleClickOutside, false);
    }

    return (): void => {
      doc.removeEventListener('click', handleClickOutside, false);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    if (selectedResult) {
      setUsersInput(false);
      closeDropdown();
        fetchPlaceDetail(selectedResult.place_id)
          ?.then((result) => {
            onGooglePlaceSelect(result);
          })
          .catch((error) => {
            console.error(error, 'Error while fetching Place Detail');
          });
    }
  }, [selectedResult, fetchPlaceDetail]);

  useEffect(() => {
    setUsersInput(false);
    setSelectedResult(null);
    setSelectedPlaceId('');
  }, [countryIsoCode]);

  useEffect(() => {
    if (searchInputValue === searchText) {
      return;
    }

    setSearchInputValue(searchText);
  }, [searchText]);

  useEffect(() => {
    if (selectedPlaceId && selectedPlaceId !== selectedResult?.place_id) {
      setSelectedResult({ place_id: selectedPlaceId, description: searchInputValue ?? '' });
    }
  }, [selectedPlaceId, selectedResult]);


  const handleEnter = () => {
    if (!suggestions) {
      return;
    }
    if (higlightedSuggestionIndex === null) {
      if (suggestions?.googlePlacesPredictions?.length > 0) {
        handleSuggestionSelect(suggestions?.googlePlacesPredictions[0]);
      }
    } else {
      const predictionsCount = suggestions?.googlePlacesPredictions?.length || 0;

      if (higlightedSuggestionIndex <= predictionsCount - 1) {
        handleSuggestionSelect(suggestions?.googlePlacesPredictions[higlightedSuggestionIndex]);
      }
    }
  };

  const handleArrowUp = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!suggestions) {
      return;
    }

    e.preventDefault();
    const resultsLength = suggestions?.googlePlacesPredictions.length;
    const index = higlightedSuggestionIndex !== null && higlightedSuggestionIndex > 0 ? higlightedSuggestionIndex - 1 : resultsLength - 1;
    setHiglightedSuggestionIndex(index);
  };

  const handleArrowDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!suggestions) {
      return;
    }

    e.preventDefault();
    const resultsLength = suggestions?.googlePlacesPredictions.length;
    const index = higlightedSuggestionIndex !== null && higlightedSuggestionIndex < resultsLength - 1 ? higlightedSuggestionIndex + 1 : 0;
    setHiglightedSuggestionIndex(index);
  };

  const keyDownHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleEnter();
      return;
    }

    if (e.key === 'ArrowDown') {
      handleArrowDown(e);
      return;
    }

    if (e.key === 'ArrowUp') {
      handleArrowUp(e);
    }
  };

  return (
    <div className={`preferred-dealer-search__text-search ${countryIsoCode ? '' : 'preferred-dealer-search__text-search--disable'}`} ref={textSearchElmRef}>
      <div className={`preferred-dealer-search__text-search__input-wrapper ${(dropdownOpen || searchInputValue) ? 'preferred-dealer-search__text-search__input-wrapper--open' : '' }`}
       onClick={inputClickHandler} // eslint-disable-line
      >
        <label
          htmlFor="search-input-with-suggestions-input"
          className={`preferred-dealer-search__text-search-label ${
            dropdownOpen || searchInputValue ? '' : 'preferred-dealer-search__text-search-label--center'
          } `}
        >
          {label}
        </label>
        <input
          type="text"
          id="search-input-with-suggestions-input"
          className="preferred-dealer-search__text-search-input"
          value={searchInputValue}
          onChange={textChangeHandler}
          onFocus={textInputFocusHandler}
          onKeyDown={keyDownHandler}
          ref={texInputElmRef}
          onClick={onClick}
        />
      </div>
     
      {dropdownOpen && suggestions && (
        <div className="preferred-dealer-search__text-search-suggestions">
          <ItemList
            items={suggestions?.googlePlacesPredictions}
            highlightedSuggestionIndex={higlightedSuggestionIndex}
            searchText={searchText}
            mouseEnterHandler={(index: number) => {
              handleSuggestionMouseEnter(index);
            }}
            clickHandler={(result: any) => {
              handleSuggestionSelect(result);
            }}
            optionTextHandler={getDisplayNameFromResult}
            keyGenerator={getDisplayNameFromResult}
          />
          <img className="preferred-dealer-search__text-search-google-logo" src={googleLogo} alt="powered by Google" />
        </div>
      )}
    </div>
  );
};

export default TextSearch;
